import React, { useCallback, useMemo } from "react";
import cx from "classnames";
import { UncontrolledTooltip } from "reactstrap";
import { useProductActions } from "stores/product";
import type { ResultOf } from "gql.tada";
import type { resultingListSubscription } from "api/subscription/product";

type ResultingListSubscriptionResult = ResultOf<
  typeof resultingListSubscription
>;

type ColumnTitle = NonNullable<
  Extract<
    NonNullable<ResultingListSubscriptionResult["productListView"]>,
    { __typename?: "ProductListView" | undefined }
  >
>["listViewData"]["columnTitles"][number];

type ListViewItem = NonNullable<
  Extract<
    NonNullable<ResultingListSubscriptionResult["productListView"]>,
    { __typename?: "ProductListView" | undefined }
  >
>["listViewData"]["productListViewItems"][number];

enum ValidationResult {
  None = "None",
  Warning = "Warning",
  Critical = "Critical",
  Information = "Information",
}

type ProductSuggestionTableRowProps = {
  listItem: ListViewItem;
  columnTitles: ColumnTitle[];
  currentRowKey: number;
};

export const ProductSuggestionTableRow = ({
  listItem,
  columnTitles,
  currentRowKey,
}: ProductSuggestionTableRowProps) => {
  const productActions = useProductActions();

  const handleProductSelection = useCallback(() => {
    productActions.loadResultListItem(listItem);
  }, [listItem, productActions]);

  const sortedProperties = useMemo(() => {
    const map = new Map(
      listItem?.properties?.map((cell) => [cell?.propertyId, cell]),
    );

    return columnTitles.map((col) => map.get(col.key)).filter((prop) => !!prop);
  }, [columnTitles, listItem.properties]);

  return (
    <tr
      onClick={handleProductSelection}
      id={`ProductSuggestionTableRow_${currentRowKey}`}
      className="cursor-pointer"
    >
      {sortedProperties?.map((property, key) => (
        <td key={key} id={`resultinglistTableCell-${currentRowKey}-${key}`}>
          <div
            className={cx("d-flex", {
              "justify-content-between fw-bold":
                property?.validationResult !== ValidationResult.None,
              "justify-content-center":
                property?.validationResult === ValidationResult.None,
            })}
          >
            {property?.validationResult === ValidationResult.Critical && (
              <div className="text-danger">X</div>
            )}
            {property.value}
          </div>
          {property.validationMessage && (
            <UncontrolledTooltip
              target={`resultinglistTableCell-${currentRowKey}-${key}`}
            >
              {property.validationMessage}
            </UncontrolledTooltip>
          )}
        </td>
      ))}
    </tr>
  );
};
